import {AutoComplete} from '@/components/SendNotification/AutoComplete'
import {CustomImageSelector} from '@/components/SendNotification/CustomImageSelector'
import {LandingPageSelector} from '@/components/SendNotification/LandingPageSelector'
import {STEP_CONFIG} from '@/config/constants'
import {useCountries} from '@/hooks/useCountries'
import {NodeTypeEnum} from '@/types/NodeTypeEnum'
import {PushStepType} from '@/types/NotificationStep'
import {GetSendPushStepConfigResponse} from '@/types/StepConfigParams'
import {getAllDynamicTagsRtnUseCase} from '@/useCases/getAllTagsUseCase'
import {getStepConfigUseCase} from '@/useCases/getStepConfigUseCase'
import {MAX_MESSAGE_LENGTH} from '@/utils/consts'
import {useQuery} from '@tanstack/react-query'
import {useIntl} from 'react-intl'

interface Props {
  pushStep: PushStepType
  setPushStep: (pushStep: PushStepType) => void
}

export default function SendPushParams({pushStep, setPushStep}: Props) {
  const {formatMessage} = useIntl()
  // const [selectedImageId, setSelectedImageId] = useState<string | null>(null)
  // const [selectedPage, setSelectedPage] = useState<string | null>(null)

  const {country} = useCountries()

  const handlePageChange = (pageId: string) => {
    const selectedPageItem = landingPageOptions.find(page => page.id === pageId)
    setPushStep({...pushStep, selectedPage: selectedPageItem})
  }

  const handleImageChange = (imageId: string) => {
    const selectedImageItem = imageOptions.find(image => image.id === imageId)
    setPushStep({...pushStep, selectedImage: selectedImageItem})
  }

  const {
    isFetching: isFetchingTags,
    data: dataTags,
    error: errorTags,
  } = useQuery({
    queryKey: ['dynamicTagsRtn', ['dynamicTagsRtn']],
    queryFn: ({signal}) => getAllDynamicTagsRtnUseCase({}, signal),
  })

  const {
    isFetching: isStepConfigFetching,
    data: stepConfigData,
    error: stepConfigError,
  } = useQuery({
    queryKey: [STEP_CONFIG.DATA_QUERY_KEY, NodeTypeEnum.sendPush, country],
    queryFn: ({signal}: {signal: AbortSignal}) =>
      getStepConfigUseCase(NodeTypeEnum.sendPush, signal) as GetSendPushStepConfigResponse,

    staleTime: 3 * 60 * 1000, // 3 minutes cache
  })

  const dynamicTagSuggestions = (dataTags?.data.messageParameters ?? [])
    .filter(item => item.active)
    .map(item => item.name)
  const landingPageOptions = stepConfigData?.landingPages ?? []
  const imageOptions = stepConfigData?.images ?? []

  return (
    <div className="h-42 flex w-full max-w-xs flex-col gap-3" data-testid="send-push-params">
      {(isFetchingTags || isStepConfigFetching) && <div>Data is Loading...</div>}
      {!(isFetchingTags || isStepConfigFetching) && !(errorTags || stepConfigError) && (
        <>
          <AutoComplete
            label={formatMessage({id: 'NOTIFICATION_BUILDER.SUBJECT_LABEL'})}
            inputId="notification-subject"
            inputValue={pushStep.subject}
            onChangeInput={(value: string) => setPushStep({...pushStep, subject: value})}
            max_message_length={MAX_MESSAGE_LENGTH}
            dynamicTagSuggestions={dynamicTagSuggestions}
            tagOffset={-100}
            required
          />
          <AutoComplete
            label={formatMessage({id: 'NOTIFICATION_BUILDER.MESSAGE_LABEL'})}
            inputId="notification-message"
            inputValue={pushStep.message}
            onChangeInput={(value: string) => setPushStep({...pushStep, message: value})}
            max_message_length={MAX_MESSAGE_LENGTH}
            dynamicTagSuggestions={dynamicTagSuggestions}
            tagOffset={-100}
            required
          />
          <LandingPageSelector
            label={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.SELECT_LANDING_PAGE'})}
            options={landingPageOptions}
            value={pushStep.selectedPage?.id || landingPageOptions[0]?.id}
            onChange={handlePageChange}
            disabled={false}
          />
          <CustomImageSelector
            label={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.SELECT_PUSH_IMAGE'})}
            options={imageOptions}
            value={pushStep.selectedImage?.id || imageOptions[0]?.id}
            onChange={handleImageChange}
            disabled={false}
          />
        </>
      )}
    </div>
  )
}
